import { INTERFACE_STEPS, parseFrameLimit, type FrameLimit, type InterfaceStep, type RenderDivisor } from "odyssey-shared";
import { localStore, readSetting, writeSetting, type SettingsStore } from "./settings.ts";

/**
 * The display settings kept per device: the render resolution, the frame limit and the Interface size.
 */

/**
 * The storage key the render resolution lives under (ADR-093 decisions 10 and 11): the divisor the viewer chose,
 * "1", "2" or "3", kept per device. A window that no longer offers the stored divisor draws at the largest it
 * does offer and leaves the stored text alone.
 */
export const RENDER_DIVISOR_KEY = "odyssey.graphics.renderDivisor";

/**
 * Reads a stored value as a render divisor.
 *
 * @param raw The stored text, or null when nothing is stored.
 * @returns The divisor, or 1 (Full) for anything that is not exactly "2" or "3".
 */
export function parseRenderDivisor(raw: string | null): RenderDivisor {
  return raw === "2" ? 2 : raw === "3" ? 3 : 1;
}

/**
 * Reads the viewer's render divisor.
 *
 * @param store Where it is kept; this browser's `localStorage` by default.
 * @returns The stored divisor, or 1 when none is stored, it is malformed or the store cannot be read.
 */
export function readRenderDivisor(store: SettingsStore | null = localStore()): RenderDivisor {
  return readSetting(RENDER_DIVISOR_KEY, parseRenderDivisor, store);
}

/**
 * Keeps the viewer's render divisor.
 *
 * @param divisor The divisor to keep.
 * @param store Where to keep it; this browser's `localStorage` by default.
 * @returns True when the store took it, false when there is no store or it refused.
 */
export function writeRenderDivisor(divisor: RenderDivisor, store: SettingsStore | null = localStore()): boolean {
  return writeSetting(RENDER_DIVISOR_KEY, String(divisor), store);
}

/**
 * The storage key the frame limit lives under (ADR-093 decision 40 and its amendment of 2026-10-13): `30`, `60`,
 * `120` or `match`, kept per device and in no graphics preset. Anything else, and nothing, reads as 60.
 */
export const FRAME_LIMIT_KEY = "odyssey.graphics.frameLimit";

/**
 * Reads the viewer's frame limit.
 *
 * @param store Where it is kept; this browser's `localStorage` by default.
 * @returns The stored choice, or 60 when none is stored, it is malformed or the store cannot be read.
 */
export function readFrameLimit(store: SettingsStore | null = localStore()): FrameLimit {
  return readSetting(FRAME_LIMIT_KEY, parseFrameLimit, store);
}

/**
 * Keeps the viewer's frame limit.
 *
 * @param limit The choice to keep.
 * @param store Where to keep it; this browser's `localStorage` by default.
 * @returns True when the store took it, false when there is no store or it refused.
 */
export function writeFrameLimit(limit: FrameLimit, store: SettingsStore | null = localStore()): boolean {
  return writeSetting(FRAME_LIMIT_KEY, limit, store);
}

/**
 * The storage key the Interface size lives under: a whole step from the automatic UI scale (ADR-061's amendment
 * "interface size"), kept per device because it depends on the screen.
 */
export const INTERFACE_STEP_KEY = "odyssey.interfaceStep";

/**
 * Reads a stored value as the Interface size step.
 *
 * @param raw The stored text, or null when nothing is stored.
 * @returns The step, -2 to 2; 0, the automatic scale, for anything else.
 */
export function parseInterfaceStep(raw: string | null): InterfaceStep {
  return INTERFACE_STEPS.find((step) => String(step) === raw) ?? 0;
}

/**
 * Reads the viewer's Interface size step.
 *
 * @param store Where it is kept, or null when there is none.
 * @returns The step.
 */
export function readInterfaceStep(store: SettingsStore | null = localStore()): InterfaceStep {
  return readSetting(INTERFACE_STEP_KEY, parseInterfaceStep, store);
}

/**
 * Keeps the viewer's Interface size step.
 *
 * @param step The step.
 * @param store Where to keep it, or null when there is none.
 * @returns True when the store took it.
 */
export function writeInterfaceStep(step: InterfaceStep, store: SettingsStore | null = localStore()): boolean {
  kept = step;
  for (const listener of listeners) {
    listener();
  }
  return writeSetting(INTERFACE_STEP_KEY, String(step), store);
}

/**
 * The step this page last read or wrote, which every surface that lays itself out reads, so the store is read once and
 * again only when another tab changes it; null until first read or after another tab wrote.
 */
let kept: InterfaceStep | null = null;

/**
 * Who is told when the step changes on this page.
 */
const listeners = new Set<() => void>();

/**
 * Whether the storage listener that drops the kept step when another tab changes it is in place.
 */
let watching = false;

/**
 * The viewer's Interface size as this page holds it: read from the store once, and kept current by
 * `writeInterfaceStep` and by another tab's change.
 *
 * @returns The step.
 */
export function keptInterfaceStep(): InterfaceStep {
  if (!watching && typeof window !== "undefined") {
    watching = true;
    window.addEventListener("storage", (event) => {
      if (event.key === null || event.key === INTERFACE_STEP_KEY) {
        kept = null;
        for (const listener of listeners) {
          listener();
        }
      }
    });
  }
  kept ??= readInterfaceStep();
  return kept;
}

/**
 * Follows the step, for `useSyncExternalStore`.
 *
 * @param listener Told after each change.
 * @returns Stops following.
 */
export function subscribeInterfaceStep(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
